export interface Game {
  id: string;
  title: string;
  category: string;
  image: string;
  description?: string;
  isNew?: boolean;
  isHot?: boolean;
  isBanner?: boolean;
  enable: boolean;
  externalUrl?: string;
  orientation?: 'portrait' | 'landscape';
  apiGameId?: number; // Playsout API game ID
}

const normalizeExternalUrl = (externalUrl?: string): string | undefined => {
  if (!externalUrl) return externalUrl;
  try {
    const normalized = new URL(externalUrl);
    if (normalized.hostname === 'games.playsout.com') {
      const path = normalized.pathname;
      const hasTrailingSlash = path.endsWith('/');
      const hasFileExtension = /\.[a-z0-9]+$/i.test(path);
      if (!hasTrailingSlash && !hasFileExtension) {
        normalized.pathname = `${path}/`;
      }
    }
    return normalized.toString();
  } catch {
    return externalUrl;
  }
};

const normalizeGames = (list: Game[]): Game[] =>
  list.map((game) => {
    if (!game.externalUrl) return game;
    const normalizedUrl = normalizeExternalUrl(game.externalUrl);
    return normalizedUrl === game.externalUrl
      ? game
      : { ...game, externalUrl: normalizedUrl };
  });

const rawGames: Game[] = [
  {
    id: "BlockCrushFun",
    title: "Block Crush Fun",
    category: "Puzzle",
    image: "/images/games/BlockCrushFun.png",
    isNew: true,
    isHot: true,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/BlockCrushFun",
  },
  {
    id: "DogeSurvivors",
    title: "Doge Survivors",
    category: "Doge OS",
    image: "/images/games/DogeSurvivors.jpg",
    isNew: true,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/Doge/DogeSurvivors/",
  },
  {
    id: "DogeMaze",
    title: "Doge Maze",
    category: "Doge OS",
    image: "/images/games/DogeMaze.jpg",
    isNew: true,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/Doge/DogeMaze/",
  },
  {
    id: "DogeMart",
    title: "Doge Mart",
    category: "Doge OS",
    image: "/images/games/DogeMart.jpg",
    isNew: true,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/Doge/DogeMart/",
  },
  {
    id: "KittyEscape",
    title: "Kitty Escape",
    category: "Puzzle",
    image: "/images/games/KittyEscape.jpg",
    isNew: true,
    isHot: true,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/KittyEscape/",
  },
  {
    id: "ProjectGarden",
    title: 'Project: Garden',
    category: 'Adventure',
    image: '/images/games/SimulatedAdventures.jpg',
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: 'https://games.playsout.com/simulated/',
  },
  {
    id: "ProjectIDLE",
    title: 'Project: IDLE',
    category: 'Simulation',
    image: '/images/ProjectIDLE.jpg',
    isNew: true,
    isHot: true,
    enable: true,
    orientation: 'portrait',
    externalUrl: 'https://games.playsout.com/idle/',
  },
  {
    id: "CulinaryStarWeb",
    title: 'Culinary Star',
    category: 'Casual',
    image: '/images/games/CulinaryStar.webp',
    description: 'Cooking game',
    isNew: false,
    isHot: true,
    enable: true,
    orientation: 'portrait',
    externalUrl: 'https://games.playsout.com/CulinaryStar/',
  },
  {
    id: "PickaxeQuest",
    title: 'Pickaxe Quest',
    category: 'Adventure',
    image: '/images/games/PickaxeQuest.jpg',
    description: 'Mining and adventure game',
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: 'https://games.playsout.com/PickaxeQuest/',
    apiGameId: 73
  },
  {
    id: "MonsterSurvivors",
    title: "Monster Survivors",
    category: "Adventure",
    image: "/images/games/MonsterSurvivors.jpg",
    isNew: false,
    isHot: true,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/MonsterSurvivors/",
  },
  {
    id: "IdleTowerDefense",
    title: "Idle Tower Defense",
    category: "RPG",
    image: "/images/games/IdleTowerDefense.jpg",
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/IdleTowerDefense/",
  },
  {
    id: "MushroomWarriors",
    title: "Mushroom Warriors",
    category: "RPG",
    image: "/images/games/MushroomWarriors.jpg",
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/MushroomWarriors/",
  },
  {
    id: "FollowThePath",
    title: "Follow The Path",
    category: "Arcade",
    image: "/images/games/FollowThePath.jpg",
    isNew: false,
    isHot: true,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/FollowThePath/",
  },
  {
    id: "ColorRun",
    title: "Color Run",
    category: "Arcade",
    image: "/images/games/ColorRun.jpg",
    isNew: false,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/ColorRun/",
  },
  {
    id: "StupidArrow",
    title: "Stupid Arrow",
    category: "Arcade",
    image: "/images/games/StupidArrow.jpg",
    isNew: false,
    isHot: true,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/StupidArrow/",
  },
  {
    id: "ZigZag",
    title: "Zig Zag",
    category: "Arcade",
    image: "/images/games/ZigZag.jpg",
    isNew: false,
    isHot: true,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/ZigZag/",
  },
  {
    id: "SpikesEverywhere",
    title: "Spikes Everywhere",
    category: "Arcade",
    image: "/images/games/SpikesEverywhere.jpg",
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/SpikesEverywhere/",
  },
  {
    id: "ArrowRacer",
    title: "Arrow Racer",
    category: "Arcade",
    image: "/images/games/ArrowRacer.jpg",
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/ArrowRacer/",
  },
  {
    id: "CircleLeap",
    title: "Circle Leap",
    category: "Arcade",
    image: "/images/games/CircleLeap.jpg",
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/CircleLeap/",
  },
  {
    id: "ColorMatcher",
    title: "Color Matcher",
    category: "Arcade",
    image: "/images/games/ColorMatcher.jpg",
    isNew: false,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/ColorMatcher/",
  },
  {
    id: "EndlessMaze",
    title: "Endless Maze",
    category: "Arcade",
    image: "/images/games/EndlessMaze.jpg",
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/EndlessMaze/",
  },
  {
    id: "SpinningAround",
    title: "Spinning Around",
    category: "Arcade",
    image: "/images/games/SpinningAround.jpg",
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/SpinningAround/",
  },
  {
    id: "BoomBallz",
    title: "Boom Ballz",
    category: "Arcade",
    image: "/images/games/BoomBallz.jpg",
    isNew: false,
    isHot: true,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/BoomBallz/",
  },
  {
    id: "CubeHead",
    title: "Cube Head",
    category: "Arcade",
    image: "/images/games/CubeHead.jpg",
    isNew: false,
    isHot: true,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/CubeHead/",
  },
  {
    id: "CapybaraLink",
    title: "Capybara Link",
    category: "Puzzle",
    image: "/images/games/CapybaraLink.jpg",
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/CapybaraLink/",
  },
  {
    id: "Snake2048",
    title: "Snake 2048",
    category: "Puzzle",
    image: "/images/games/Snake2048.jpg",
    isNew: true,
    isHot: true,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/Snake2048/",
  },
  {
    id: "FishTankFury",
    title: "Fish Tank Fury",
    category: "Puzzle",
    image: "/images/games/FishTankFury.jpg",
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/FishTankFury/",
  },
  {
    id: "ConnectBalls",
    title: "Connect Balls",
    category: "Arcade",
    image: "/images/games/ConnectBalls.jpg",
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/ConnectBalls/",
  },
  {
    id: "CircularBreaker",
    title: "Circular Breaker",
    category: "Arcade",
    image: "/images/games/CircularBreaker.jpg",
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/CircularBreaker/",
  },
  {
    id: "OneDoor",
    title: "One Door",
    category: "Arcade",
    image: "/images/games/OneDoor.jpg",
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/OneDoor/",
  },
  {
    id: "CatchtheFruits",
    title: "Catch the Fruits",
    category: "Casual",
    image: "/images/games/CatchtheFruits.jpg",
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/CatchtheFruits/",
  },
  {
    id: "Falling2048",
    title: "Falling 2048",
    category: "Casual",
    image: "/images/games/Falling2048.jpg",
    isNew: true,
    isHot: false,
    enable: true,
    orientation: 'portrait',
    externalUrl: "https://games.playsout.com/Falling2048/",
  },
];

export const games: Game[] = normalizeGames(rawGames);

// 获取所有启用的游戏
export const getEnabledGames = (): Game[] => {
  return games.filter(game => game.enable);
};

// 按类别分组的游戏（只包含启用的游戏）
export const gamesByCategory = {
  'Adventure': getEnabledGames().filter(game => game.category === 'Adventure'),
  'Moba': getEnabledGames().filter(game => game.category === 'Moba'),
};

// 获取新游戏（只包含启用的游戏）
export const getNewGames = (): Game[] => {
  return getEnabledGames().filter(game => game.isNew);
};

// 获取热门游戏（返回所有启用的游戏，确保首页显示完整）
export const getHotGames = (): Game[] => {
  return getEnabledGames();
};

// 获取真正标记为热门的游戏（严格按照 isHot 标记过滤）
export const getMarkedHotGames = (): Game[] => {
  return getEnabledGames().filter(game => game.isHot);
};

// 获取Banner游戏（只包含启用的游戏）
export const getBannerGames = (): Game[] => {
  return getEnabledGames().filter(game => game.isBanner);
};

// 根据ID获取游戏（包含禁用的游戏，用于直接访问）
export const getGameById = (id: string): Game | undefined => {
  return games.find(game => game.id === id);
};

// 根据游戏ID获取API游戏ID
export const getApiGameId = (id: string): number | undefined => {
  const game = games.find(game => game.id === id);
  return game?.apiGameId;
};

// 根据ID获取启用的游戏
export const getEnabledGameById = (id: string): Game | undefined => {
  return getEnabledGames().find(game => game.id === id);
};

// 获取所有游戏类别（只包含启用游戏的类别）
export const getAllCategories = (): string[] => {
  return Array.from(new Set(getEnabledGames().map(game => game.category)));
};

// 搜索游戏（只在启用的游戏中搜索）
export const searchGames = (query: string): Game[] => {
  const lowercaseQuery = query.toLowerCase();
  return getEnabledGames().filter(game =>
    game.title.toLowerCase().includes(lowercaseQuery) ||
    game.category.toLowerCase().includes(lowercaseQuery) ||
    (game.description && game.description.toLowerCase().includes(lowercaseQuery))
  );
};
